import { ethers } from 'ethers';
import { ProviderManager } from './ProviderManager';
import { NetworkManager } from './NetworkManager';
import { GasManager } from './GasManager';
import { SafeMath } from '../utils/SafeMath';
import contractABI from '../abi/contract.json';
import multicallABI from '../abi/multicall.json';
import { ENV } from '../constants/environment';

// Same on all chains!
const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11';

export class ContractService {
    constructor() {
        this.providerManager = null;
        this.contract = null;
        this.userAddress = null;
        this.multicall = null;
    }

    async initialize(address = null) {
        const network = NetworkManager.getTargetNetwork();

        if (address) {
            // Connected mode with wallet
            this.providerManager = ProviderManager.getInstance();
            await this.providerManager.initialize();
            const signer = this.providerManager.getSigner();
            this.contract = new ethers.Contract(ENV.CONTRACT_ADDRESS, contractABI, signer);
            this.multicall = new ethers.Contract(MULTICALL3_ADDRESS, multicallABI, signer);
            this.userAddress = address;

            const calls = [
                {
                    target: this.contract.address,
                    callData: this.contract.interface.encodeFunctionData('whitelistedAddresses', [address])
                },
                {
                    target: this.contract.address,
                    callData: this.contract.interface.encodeFunctionData('whitelistMint')
                }
            ];

            const [, returnData] = await this.multicall.callStatic.aggregate(calls);
            const [isWhitelisted, isWhitelistActive] = returnData.map((data, index) =>
                this.contract.interface.decodeFunctionResult(
                    index === 0 ? 'whitelistedAddresses' : 'whitelistMint',
                    data
                )[0]
            );

            return { isWhitelisted, isWhitelistActive };
        } else {
            // Read-only mode
            this.providerManager = null;
            const provider = new ethers.providers.JsonRpcProvider(network.rpcUrl);
            this.contract = new ethers.Contract(ENV.CONTRACT_ADDRESS, contractABI, provider);
            this.multicall = new ethers.Contract(MULTICALL3_ADDRESS, multicallABI, provider);
            this.userAddress = null;
            return { isWhitelisted: false, isWhitelistActive: false };
        }
    }

    async validateNetworkForTransaction() {
        if (!this.providerManager) return; // Skip validation in read-only mode

        const network = await this.providerManager.getNetwork();
        const targetNetwork = NetworkManager.getTargetNetwork();

        if (network.chainId !== targetNetwork.chainId) {
            throw new Error(`Please switch to ${targetNetwork.name} network`);
        }
    }

    async getContractState() {
        try {
            // Only validate network if we have a provider manager
            if (this.providerManager) {
                await this.validateNetworkForTransaction();
            }

            const calls = [
                'whitelistMint',
                'publicMint',
                'supplyLimit',
                'totalMinted',
                'maxLimitPerWallet',
                'price'
            ].map(method => ({
                target: this.contract.address,
                callData: this.contract.interface.encodeFunctionData(method)
            }));

            // Add whitelist check if user address exists
            if (this.userAddress) {
                calls.push({
                    target: this.contract.address,
                    callData: this.contract.interface.encodeFunctionData('whitelistedAddresses', [this.userAddress])
                });
            }

            const [, returnData] = await this.multicall.callStatic.aggregate(calls);

            const [
                whitelistMint,
                publicMint,
                supplyLimit,
                totalMinted,
                maxLimitPerWallet,
                price,
                isWhitelisted
            ] = returnData.map((data, index) => {
                const methodName = index === 6 ? 'whitelistedAddresses' : [
                    'whitelistMint',
                    'publicMint',
                    'supplyLimit',
                    'totalMinted',
                    'maxLimitPerWallet',
                    'price'
                ][index];
                return this.contract.interface.decodeFunctionResult(methodName, data)[0];
            });
            return {
                whitelistMint,
                publicMint,
                supplyLimit: supplyLimit.toString(),
                totalMinted: totalMinted.toString(),
                maxLimitPerWallet: maxLimitPerWallet.toString(),
                price: SafeMath.formatEther(price),
                isWhitelisted: this.userAddress ? isWhitelisted : false
            };
        } catch (error) {
            console.error('Contract state fetch error:', error);
            throw new Error(error.reason || 'Failed to fetch contract state');
        }
    }

    async estimateMintGas(concept, amount, chainNo, value) {
        await this.validateNetworkForTransaction();

        return await GasManager.estimateGas(
            this.contract,
            'mint',
            [this.userAddress, concept, amount, chainNo],
            value
        );
    }

    async estimateClaimGas(concept, chainNo, value) {
        await this.validateNetworkForTransaction();

        return await GasManager.estimateGas(
            this.contract,
            'whitelistmint',
            [this.userAddress, concept, chainNo],
            value
        );
    }

    async whitelistMint(concept, chainNo, gasConfig) {
        try {
            await this.providerManager.validateAndSwitchNetwork();

            const tx = await this.contract.whitelistmint(
                this.userAddress,
                concept,
                chainNo,
                {
                    value: 0,
                    gasLimit: gasConfig.gasLimit,
                    // maxFeePerGas: gasConfig.maxFeePerGas,
                    // maxPriorityFeePerGas: gasConfig.maxPriorityFeePerGas
                }
            );

            return tx;
        } catch (error) {
            console.error('Whitelist mint error:', error);
            if (error.code === 'ACTION_REJECTED') {
                throw new Error('Transaction rejected by user');
            }
            throw new Error(error.reason || 'Whitelist mint failed');
        }
    }

    async mint(concept, amount, chainNo, value, gasConfig) {
        try {
            await this.providerManager.validateAndSwitchNetwork();

            const tx = await this.contract.mint(
                this.userAddress,
                concept,
                amount,
                chainNo,
                {
                    value,
                    gasLimit: gasConfig.gasLimit,
                    // maxFeePerGas: gasConfig.maxFeePerGas,
                    // maxPriorityFeePerGas: gasConfig.maxPriorityFeePerGas
                }
            );

            return tx;
        } catch (error) {
            console.error('Mint error:', error);
            if (error.code === 'ACTION_REJECTED') {
                throw new Error('Transaction rejected by user');
            }
            throw new Error(error.reason || 'Mint failed');
        }
    }

    async getVotingData() {
        try {
            const methods = [
                'concept1Minted',
                'concept2Minted',
                'concept3Minted',
                'concept4Minted',
                'concept5Minted',
                'ArbitrumVoted',
                'OptimismVoted',
                'BaseVoted',
                'LineaVoted',
                'SolanaVoted',
                'ZksyncVoted',
                'MantleVoted',
                'ModeVoted'
            ];

            const calls = methods.map(method => ({
                target: this.contract.address,
                callData: this.contract.interface.encodeFunctionData(method)
            }));

            const [, returnData] = await this.multicall.callStatic.aggregate(calls);

            const decodedResults = returnData.map((data, index) =>
                Number(this.contract.interface.decodeFunctionResult(methods[index], data)[0])
            );

            return {
                concepts: {
                    1: decodedResults[0],
                    2: decodedResults[1],
                    3: decodedResults[2],
                    4: decodedResults[3],
                    5: decodedResults[4]
                },
                chains: {
                    1: decodedResults[5],
                    2: decodedResults[6],
                    3: decodedResults[7],
                    4: decodedResults[8],
                    5: decodedResults[9],
                    6: decodedResults[10],
                    7: decodedResults[11],
                    8: decodedResults[12]
                }
            };
        } catch (error) {
            console.error('Voting data fetch error:', error);
            throw new Error('Failed to fetch voting data');
        }
    }
}